import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase-auth';
import { useAuth } from '../../contexts/AuthContextLazy';
import {
  ExclamationTriangleIcon,
  ClockIcon,
  UserGroupIcon,
  ChevronRightIcon,
  BellAlertIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';

interface ClientNeedingAttention {
  id: string;
  name: string;
  type: string;
  status: string;
  assigned_advisor_id: string;
  days_since_contact: number | null;
  last_interaction_type: string | null;
  last_interaction_date: string | null;
  engagement_score: number;
  contact_frequency: string;
  attention_status: string;
  priority: string;
}

export const ClientsNeedingAttention: React.FC = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState<ClientNeedingAttention[]>([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(5);

  useEffect(() => {
    fetchClientsNeedingAttention();
  }, []);

  const fetchClientsNeedingAttention = async () => {
    try {
      const { data, error } = await supabase
        .from('clients_needing_attention')
        .select('*')
        .limit(limit);

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching clients needing attention:', error);
        }

      }
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-100';
      case 'high':
        return 'text-orange-600 bg-orange-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Never contacted':
        return <BellAlertIcon className="h-5 w-5 text-red-500" />;
      case 'Overdue':
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  if (loading) {
    return (
      <div className="bg-white portal-content-shadow rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <UserGroupIcon className="h-6 w-6 text-amber-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Clients Needing Attention</h3>
          </div>
          <span className="text-sm text-gray-500">{clients.length} clients</span>
        </div>
      </div>

      {clients.length === 0 ? (
        <div className="px-6 py-8 text-center">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">All clients are up to date!</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {clients.map((client) => (
            <Link
              key={client.id}
              to={`/portal/clients/${client.id}`}
              className="block px-6 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center min-w-0">
                  <div className="flex-shrink-0 mr-3">
                    {getStatusIcon(client.attention_status)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate">{client.name}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(client.priority)}`}>
                        {client.priority}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        {client.days_since_contact === null
                          ? 'Never contacted'
                          : client.days_since_contact === 0
                          ? 'Today'
                          : `${client.days_since_contact} days ago`}
                      </span>
                      {client.last_interaction_type && (
                        <span>Last: {client.last_interaction_type}</span>
                      )}
                      {client.engagement_score < 50 && (
                        <span className="text-red-600">Low engagement ({client.engagement_score}%)</span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-600">{client.attention_status}</p>
                  </div>
                </div>
                <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {clients.length > 0 && (
        <div className="px-6 py-3 bg-gray-50 text-center">
          <Link
            to="/portal/clients?filter=needs-attention"
            className="text-sm text-amber-600 hover:text-amber-700 font-medium"
          >
            View all clients needing attention →
          </Link>
        </div>
      )}
    </div>
  );
};